import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    Button,
    Box,
    Snackbar,
    Alert,
    useMediaQuery,
    TablePagination,
    Stack,
    Chip,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';

import TableComponent from '../../widgets/TableComponent';
import ViewNotificationCampaignDialog from '../../widgets/notification/ViewNotificationCampaignDialog';
import NewNotificationCampaignDialog from '../../widgets/notification/NewNotificationCampaignDialog';
import EditNotificationCampaignDialog from '../../widgets/notification/EditNotificationCampaignDialog';
import DeleteNotificationCampaignDialog from '../../widgets/notification/DeleteNotificationCampaignDialog';
import NotificationCampaignList from '../../widgets/notification/NotificationCampaignList';

import { formatDate } from '../../utils/formatDate';

const NotificationCampaignsPage = () => {
    const { user, hasPermission } = useContext(UserContext);

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchCampaigns = async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await RequestService.getNotificationCampaigns({
                page,
                size: rowsPerPage,
            });

            setCampaigns(data.content || []);
            setTotalElements(data.totalElements ?? 0);
        } catch (err) {
            // console.log(err);
            setError('Erro ao carregar campanhas de notificação.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
        // eslint-disable-next-line
    }, [page, rowsPerPage]);

    const handleRowClick = (campaign) => {
        setSelectedCampaign(campaign);
        setIsViewDialogOpen(true);
    };

    const handleCloseViewDialog = () => {
        setIsViewDialogOpen(false);
        setSelectedCampaign(null);
    };

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);

    const handleOpenEditDialog = () => {
        setIsEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
    };

    const handleSuccessAdd = async (message) => {
        await fetchCampaigns();
        setSnackbarMessage(message || 'Campanha criada com sucesso!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleSuccessEdit = async (message) => {
        await fetchCampaigns();
        setSnackbarMessage(message || 'Campanha atualizada com sucesso!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setIsEditDialogOpen(false);
    };

    const handleOpenDeleteDialog = () => setIsDeleteDialogOpen(true);
    const handleCloseDeleteDialog = () => setIsDeleteDialogOpen(false);

    const handleDeleteCampaign = async (campaignId) => {
        try {
            await RequestService.deleteNotificationCampaign(campaignId);
            await fetchCampaigns();
            setSnackbarMessage('Campanha excluída com sucesso!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setIsDeleteDialogOpen(false);
            setIsViewDialogOpen(false);
            setSelectedCampaign(null);
        } catch (error) {
            console.error('Erro ao excluir campanha:', error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data ||
                'Erro ao excluir campanha.';
            setSnackbarMessage(
                typeof errorMessage === 'string'
                    ? errorMessage
                    : 'Erro ao excluir campanha.'
            );
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            throw error;
        }
    };

    const handleSendCampaign = async (campaignId) => {
        try {
            await RequestService.sendNotificationCampaign(campaignId);
            await fetchCampaigns();
            setSnackbarMessage('Campanha enviada com sucesso!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            handleCloseViewDialog();
        } catch (error) {
            console.error('Erro ao enviar campanha:', error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data ||
                'Erro ao enviar campanha.';
            setSnackbarMessage(
                typeof errorMessage === 'string'
                    ? errorMessage
                    : 'Erro ao enviar campanha.'
            );
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleSnackbarClose = () => setSnackbarOpen(false);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const StatusChip = ({ sentAt }) => (
        <Chip
            label={sentAt ? 'Enviada' : 'Pendente'}
            size='small'
            color={sentAt ? 'success' : 'warning'}
            variant={sentAt ? 'filled' : 'outlined'}
        />
    );

    const ActionTypeLabel = ({ actionType }) => {
        const labels = {
            EVENT: 'Evento',
            CONTENT: 'Conteúdo',
            PRODUCT: 'Produto',
            URL: 'URL',
            NONE: 'Nenhuma',
        };
        return labels[actionType] || actionType;
    };

    const columns = [
        {
            label: 'Título',
            field: 'title',
            align: 'left',
            renderCell: (value, row) => (
                <Stack direction='column' spacing={0.5}>
                    <Typography variant='body2' sx={{ color: '#292929' }}>
                        {row.title}
                    </Typography>
                    <Typography variant='caption' sx={{ color: '#777' }}>
                        {row.message?.slice(0, 60) || '-'}
                        {row.message?.length > 60 ? '...' : ''}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: 'Tipo',
            field: 'type',
            align: 'center',
            renderCell: (value, row) => (
                <Stack
                    direction='column'
                    spacing={0.5}
                    sx={{ alignItems: 'center' }}
                >
                    <Typography
                        variant='body2'
                        sx={{ fontSize: '14px', color: '#292929' }}
                    >
                        <ActionTypeLabel actionType={row.actionType} />
                    </Typography>
                </Stack>
            ),
        },
        {
            label: 'Status',
            field: 'sentAt',
            align: 'center',
            renderCell: (value, row) => <StatusChip sentAt={row.sentAt} />,
        },
        {
            label: 'Criada em',
            field: 'createdAt',
            align: 'center',
            renderCell: (value, row) => (
                <Typography variant='body2' sx={{ fontSize: '14px' }}>
                    {formatDate(row.createdAt)}
                </Typography>
            ),
        },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row) => (
                <Stack direction='row' spacing={1} justifyContent='flex-end'>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(row);
                        }}
                        size='small'
                    >
                        Visualizar
                    </Button>
                </Stack>
            ),
        },
    ];

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    const renderEmptyState = () => (
        <Box
            sx={{
                textAlign: 'center',
                mt: 4,
                color: '#666',
                fontFamily: 'Poppins',
                fontSize: 18,
            }}
        >
            <Typography variant='h6'>
                Nenhuma campanha de notificação cadastrada.
            </Typography>
            <Typography variant='body2'>
                Clique em <strong>"Nova Mensagem"</strong> para adicionar a
                primeira!
            </Typography>
        </Box>
    );

    const renderMobileLayout = () => {
        return campaigns.length === 0 ? (
            renderEmptyState()
        ) : (
            <NotificationCampaignList
                campaigns={campaigns}
                onClick={handleRowClick}
            />
        );
    };

    const renderDesktopLayout = () => {
        if (campaigns.length === 0) return renderEmptyState();

        return (
            <Box>
                <TableComponent columns={columns} rows={campaigns} />

                <TablePagination
                    component='div'
                    count={totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Box>
        );
    };

    return (
        <Box>
            <Grid container justifyContent='start'>
                <Breadcrumbs aria-label='breadcrumb'>
                    <Link underline='hover' color='inherit' href='/'>
                        Início
                    </Link>
                    <Link underline='hover' color='inherit'>
                        Definições
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Mensagens
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Mensagens
                </Typography>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 12,
                        fontWeight: '400',
                        color: '#292929',
                        mb: 2,
                    }}
                >
                    Mensagens geram notificações para todos os usuários após o
                    seu envio. As mensagens podem ser de temas livres ou
                    relacionadas à um Evento previamente cadastrado.
                </Typography>
            </Grid>

            <Grid container sx={12} justifyContent={'end'}>
                <Grid item sx={6}>
                    {hasPermission('NOTIFICATION_CREATE') && (
                        <Button
                            variant='contained'
                            sx={{ minHeight: '47px', mb: 2 }}
                            endIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={handleOpenAddDialog}
                        >
                            Nova Mensagem
                        </Button>
                    )}
                </Grid>
            </Grid>

            {isMobile ? renderMobileLayout() : renderDesktopLayout()}

            <NewNotificationCampaignDialog
                open={isAddDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') handleCloseAddDialog();
                }}
                onSuccess={handleSuccessAdd}
                user={user}
            />

            <ViewNotificationCampaignDialog
                open={isViewDialogOpen}
                onClose={handleCloseViewDialog}
                campaign={selectedCampaign}
                onSend={handleSendCampaign}
                onEdit={handleOpenEditDialog}
                canEdit={hasPermission('NOTIFICATION_UPDATE')}
                onDelete={handleOpenDeleteDialog}
                canDelete={hasPermission('NOTIFICATION_DELETE')}
            />

            <EditNotificationCampaignDialog
                open={isEditDialogOpen}
                onClose={handleCloseEditDialog}
                onSuccess={handleSuccessEdit}
                campaign={selectedCampaign}
            />

            <DeleteNotificationCampaignDialog
                open={isDeleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                campaign={selectedCampaign}
                onConfirm={handleDeleteCampaign}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    variant='filled'
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default NotificationCampaignsPage;
